import Button from '../button/Button';
import { useEffect, useRef, useState } from 'react';
import { useOrderInfoContext } from '@src/app/order-info-context';
import { orderFlowCloseReason, useOrderFlowContext } from '@src/app/order-flow-context';
import Svg from '@src/components/svg/Svg';
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';

export type SizeMeasurementItemModel = {
  imgPath: string;
  name: string;
  text: string;
  duration: string;
  value: string;
  url?: string;
};

export interface SizeMeasurementModel {
  type: string;
  title: string;
  placeholder: string;
  ctaText: string;
  collectingFromPostcodeText: string;
  changePostcodeText: string;
  items: SizeMeasurementItemModel[];
  carousel?: boolean;
}

interface Props {
  data: SizeMeasurementModel;
}

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { BREAKPOINT_DESKTOP, BREAKPOINT_TABLET } from '@src/hooks/use-breakpoint';

const SizeMeasurement = ({ data }: Props) => {
  const { productCode, setProductCode, setDescription } = useOrderInfoContext();
  const [cardActive, setCardActive] = useState<String>(productCode || '');
  const [urlDontknow, setURLDontknow] = useState('');

  useEffect(() => {
    const optDontknow = data.items.filter((item) => {
      return item.value === 'DONTKNOW';
    });
    if (optDontknow.length > 0) setURLDontknow(optDontknow[0].url as string);
  }, []);

  const toggleCardItem = (cardItem: SizeMeasurementItemModel) => {
    if (cardItem.value === 'DONTKNOW') {
      window.location.href = urlDontknow;
    }
    setCardActive(cardItem.value);
    setProductCode!(cardItem.value);
    setDescription!(cardItem.name);
  };

  const { handleContinue, handleClose } = useOrderFlowContext();

  // carousel
  const swiperRef = useRef<HTMLDivElement>(null);
  const isCarousel = data.carousel && data.items.length > 3;

  useEffect(() => {
    const swiperEl = swiperRef.current;
    let swiper: Swiper;

    if (!swiperEl) {
      return;
    }

    if (isCarousel) {
      swiper = new Swiper(swiperEl, {
        modules: [Navigation, Pagination],
        slidesPerView: 1,
        slidesPerGroup: 1,
        breakpoints: {
          [BREAKPOINT_TABLET]: {
            slidesPerView: 2,
            slidesPerGroup: 2,
          },
          [BREAKPOINT_DESKTOP]: {
            slidesPerView: 3,
            slidesPerGroup: 3,
          },
        },
        navigation: {
          nextEl: '.swiper-button-next-unique',
          prevEl: '.swiper-button-prev-unique',
        },
        pagination: {
          el: '.swiper-pagination-unique',
          type: 'bullets',
          clickable: true,
        },
      });
    }

    return () => {
      swiper && swiper.destroy(true, false);
    };
  }, [data.carousel, data.items, swiperRef]);

  return (
    <div className={`fm-o-size-measurement ${!isCarousel ? 'no-swipe' : ''}`}>
      <h2 className="fm-o-size-measurement__title">{data.title}</h2>

      <div className="fm-o-size-measurement__content">
        <div className={`fm-o-size-measurement__swiper ${isCarousel ? 'swiper' : ''}`} ref={swiperRef}>
          <div className={`fm-o-size-measurement__card-container ${isCarousel ? 'swiper-wrapper' : ''}`}>
            {data.items.map((cardItem) => (
              <div className={`fm-o-size-measurement__card-item ${isCarousel ? 'swiper-slide' : ''}`}>
                <div
                  className={
                    'fm-o-size-measurement__card' +
                    (cardItem.duration.length === 0 ? ' fm-o-size-measurement__card--dontknow' : '') +
                    (cardActive === cardItem.value ? ' fm-o-size-measurement__card--active' : '')
                  }
                  key={cardItem.value}
                  onClick={() => toggleCardItem(cardItem)}
                >
                  <div className="fm-o-size-measurement__card-image">
                    <img src={cardItem.imgPath} alt="" />
                  </div>

                  <div className="fm-o-size-measurement__card-content">
                    <div className="fm-o-size-measurement__card-title">{cardItem.name}</div>

                    <div className="fm-o-size-measurement__card-text">{cardItem.text}</div>

                    {cardItem.duration.length > 0 && (
                      <div className="fm-o-size-measurement__card-duration">
                        <Svg iconPath="/assets/images/new-icons.svg#clock" viewBoxWidth={16} viewBoxHeight={16} />

                        {cardItem.duration}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {isCarousel && <div className="swiper-pagination-unique"></div>}

        {isCarousel && (
          <div className="swiper-button-prev-unique">
            <Svg iconPath={'#arrow-down'} viewBoxWidth={24} viewBoxHeight={25} />
          </div>
        )}

        {isCarousel && (
          <div className="swiper-button-next-unique">
            <Svg iconPath={'#arrow-down'} viewBoxWidth={24} viewBoxHeight={25} />
          </div>
        )}
      </div>

      {!productCode && (
        <div className="fm-o-size-measurement__postcode-region">
          <div className="fm-o-size-measurement__postcode">{data.collectingFromPostcodeText}</div>

          <div className="fm-o-size-measurement__change-postcode" onClick={() => handleClose(orderFlowCloseReason.changePostCodeOrLocation)}>
            {data.changePostcodeText}
          </div>
        </div>
      )}

      {productCode && (
        <div className="fm-o-size-measurement__actions">
          <Button text={data?.ctaText} onClick={handleContinue} mainClass="fm-o-size-measurement__setup-btn" styleModifier={['fm-a-button--blue']} />
        </div>
      )}
    </div>
  );
};

export default SizeMeasurement;
